/**
 * Based on https://ui.shadcn.com/docs/components/radio-group
 * Modified with improved TypeScript support and custom styling
 */

import * as React from 'react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { CircleIcon } from 'lucide-react';

import { cn } from '@/lib/utils';

interface RadioGroupProps
  extends React.ComponentProps<typeof RadioGroupPrimitive.Root> {
  name?: string;
}

function RadioGroup({ className, name, ...props }: RadioGroupProps) {
  return (
    <RadioGroupPrimitive.Root
      data-slot="radio-group"
      name={name}
      className={cn(
        // Layout
        'grid gap-3',
        className
      )}
      {...props}
    />
  );
}

function RadioGroupItem({
  className,
  ...props
}: React.ComponentProps<typeof RadioGroupPrimitive.Item>) {
  return (
    <RadioGroupPrimitive.Item
      data-slot="radio-group-item"
      className={cn(
        // Layout & Sizing
        'aspect-square size-4 shrink-0 rounded-full',

        // Colors & Background
        'border border-input bg-white dark:bg-input/30',

        // Effects & Transitions
        'shadow-xs transition-[color,box-shadow]',

        // States
        'cursor-pointer disabled:cursor-not-allowed disabled:opacity-50',

        // Focus States
        'outline-none focus-visible:ring-[3px] focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:border-blue-500 focus-visible:ring-2 focus-visible:ring-blue-500',

        // Validation States
        'aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive',

        className
      )}
      {...props}
    >
      <RadioGroupPrimitive.Indicator
        data-slot="radio-group-indicator"
        className="relative flex items-center justify-center"
      >
        <CircleIcon className="fill-primary absolute top-1/2 left-1/2 size-2 -translate-x-1/2 -translate-y-1/2" />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  );
}

export { RadioGroup, RadioGroupItem };
